/**
 * Assignee CRUD + user-mapping helpers.
 *
 * The assignee list lives at `/projects/{pid}/assignees`. The optional
 * 1:1 link from an assignee to a real Firebase Auth user is a sibling
 * subcollection at `/projects/{pid}/assigneeUserMap`, keyed by
 * `assigneeId`. We split mapping into its own collection (instead of an
 * inline field on the assignee doc) for two reasons:
 *
 *   - Different write permissions: assignee CRUD is `editor+`, but
 *     user-binding is `admin+` (since it grants "this work counts as
 *     mine" to a member).
 *   - Cheap 1:1-from-user-side enforcement via a `where('userId', '==')`
 *     pre-flight query in this module + a rules-side guard.
 */
import {
  collection,
  deleteField,
  doc,
  deleteDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Firestore,
} from 'firebase/firestore';
import {
  type AssigneeDoc,
  type AssigneeUserMappingDoc,
  type ProjectDoc,
  type UserId,
  assigneeColorIndex,
} from '@mct/shared';
import { memberFieldsFromProject } from './firestore';

export const assigneesCol = (db: Firestore, pid: string) =>
  collection(db, 'projects', pid, 'assignees');

export const assigneeRef = (db: Firestore, pid: string, aid: string) =>
  doc(db, 'projects', pid, 'assignees', aid);

export const assigneeUserMapCol = (db: Firestore, pid: string) =>
  collection(db, 'projects', pid, 'assigneeUserMap');

export const assigneeUserMapRef = (db: Firestore, pid: string, aid: string) =>
  doc(db, 'projects', pid, 'assigneeUserMap', aid);

export interface CreateAssigneeInput {
  /** Trimmed display name. Uniqueness is *not* enforced server-side; UI guards. */
  name: string;
  /** Optional override; defaults to `assigneeColorIndex(name)`. */
  colorIndex?: number;
  /** Provenance tag. Defaults to `'manual'` for human-driven creates. */
  source?: AssigneeDoc['source'];
}

/**
 * Create a new assignee under the project. Returns the new assignee's
 * Firestore doc ID.
 *
 * The color slot is frozen at creation time (see `AssigneeDoc.colorIndex`)
 * so renaming an assignee never reshuffles their color across views.
 */
export async function createAssignee(
  db: Firestore,
  project: Pick<ProjectDoc, 'projectId' | 'memberUserIds' | 'memberRoles'>,
  input: CreateAssigneeInput,
): Promise<string> {
  const trimmed = input.name.trim();
  if (!trimmed) throw new Error('担当者名は必須です');
  const ref = doc(assigneesCol(db, project.projectId));
  const colorIndex = input.colorIndex ?? assigneeColorIndex(trimmed);
  const now = serverTimestamp();
  const payload: Omit<AssigneeDoc, 'createdAt' | 'updatedAt'> & {
    createdAt: ReturnType<typeof serverTimestamp>;
    updatedAt: ReturnType<typeof serverTimestamp>;
  } = {
    assigneeId: ref.id,
    projectId: project.projectId,
    name: trimmed,
    colorIndex,
    source: input.source ?? 'manual',
    ...memberFieldsFromProject(project),
    createdAt: now,
    updatedAt: now,
  };
  await setDoc(ref, payload);
  return ref.id;
}

/**
 * Create a brand-new assignee AND its Backlog link in one batch — the
 * "Backlog ユーザー一覧から担当者を追加" shortcut. Used to onboard people
 * who exist in Backlog but the Migratus assignee roster never heard of
 * (the long tail the fuzzy backfill couldn't resolve because there was no
 * assignee to match). Provenance is `'import'` since the name came from
 * Backlog, not a human typing it.
 *
 * Atomic: both docs commit together so we never leave an assignee without
 * its link (or vice-versa). Returns the new assigneeId.
 */
export async function createAssigneeWithBacklogUser(
  db: Firestore,
  project: Pick<ProjectDoc, 'projectId' | 'memberUserIds' | 'memberRoles'>,
  backlog: BacklogUserLink,
  actorUserId: UserId,
  colorIndex?: number,
): Promise<string> {
  const name = backlog.name.trim();
  if (!name) throw new Error('Backlog ユーザー名が空です');
  const aRef = doc(assigneesCol(db, project.projectId));
  const mRef = assigneeUserMapRef(db, project.projectId, aRef.id);
  const now = serverTimestamp();
  const member = memberFieldsFromProject(project);

  const batch = writeBatch(db);
  batch.set(aRef, {
    assigneeId: aRef.id,
    projectId: project.projectId,
    name,
    colorIndex: colorIndex ?? assigneeColorIndex(name),
    source: 'import',
    ...member,
    createdAt: now,
    updatedAt: now,
  });
  batch.set(mRef, {
    assigneeId: aRef.id,
    projectId: project.projectId,
    backlogUserId: backlog.id,
    backlogUserName: backlog.name,
    ...member,
    createdAt: now,
    updatedAt: now,
    createdByUserId: actorUserId,
  });
  await batch.commit();
  return aRef.id;
}

export async function renameAssignee(
  db: Firestore,
  pid: string,
  assigneeId: string,
  nextName: string,
): Promise<void> {
  const trimmed = nextName.trim();
  if (!trimmed) throw new Error('担当者名は必須です');
  await updateDoc(assigneeRef(db, pid, assigneeId), {
    name: trimmed,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Persist a new `colorIndex` for an assignee. Doesn't touch the name —
 * the chip's color is the only field we mutate here, so the rest of
 * the app (denormalised displays, indexed queries) stays unchanged.
 *
 * The new index is *not* range-checked here: `colorClassesByIndex`
 * wraps with modulus on render, so a transient index outside the
 * current palette length still renders a sensible color while the
 * picker UI keeps the user inside [0, ASSIGNEE_COLOR_SLOTS).
 */
export async function updateAssigneeColorIndex(
  db: Firestore,
  pid: string,
  assigneeId: string,
  colorIndex: number,
): Promise<void> {
  if (!Number.isFinite(colorIndex)) {
    throw new Error('colorIndex must be a finite number');
  }
  await updateDoc(assigneeRef(db, pid, assigneeId), {
    colorIndex: Math.trunc(colorIndex),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteAssignee(
  db: Firestore,
  pid: string,
  assigneeId: string,
): Promise<void> {
  // Best-effort cleanup of the mapping doc — keep them in sync so a
  // later create-with-same-id never inherits a stale link.
  const batch = writeBatch(db);
  batch.delete(assigneeRef(db, pid, assigneeId));
  batch.delete(assigneeUserMapRef(db, pid, assigneeId));
  await batch.commit();
}

/* ---------------------------------------------------------------------- */
/*  User mapping (1:1)                                                     */
/* ---------------------------------------------------------------------- */

export type SetMappingResult =
  | { ok: true }
  | { ok: false; reason: 'duplicate'; existingAssigneeId: string };

/**
 * Bind / rebind / unbind an assignee to a real project member.
 *
 * Pre-flight query enforces the 1:1-from-user-side constraint: the same
 * `userId` cannot appear under two different `assigneeUserMap` docs in
 * the same project. The Firestore Rules carry a matching guard, so a
 * malicious client that bypasses this helper still gets rejected.
 *
 * Pass `userId === null` to unlink (deletes the mapping doc).
 *
 * `priorMappingExists` tells us whether to take the create or update
 * branch WITHOUT a pre-flight `getDoc(ref)`. The earlier implementation
 * issued that probe to distinguish create vs update; in production it
 * triggered a `permission-denied` on the very first bind because the
 * rules-side read rule references `resource.data.memberUserIds`, which
 * is `null` when the doc does not yet exist — Firestore rejects the
 * read of a missing document and the helper never reached the actual
 * write. Callers already have the canonical answer in hand: the
 * `useAssigneeUserMappings` listener emits the live snapshot. Pass
 * `userByAssigneeId.has(assigneeId)` (or equivalent) so the helper can
 * branch without touching the network for nothing.
 */
export async function setAssigneeUserMapping(
  db: Firestore,
  project: Pick<ProjectDoc, 'projectId' | 'memberUserIds' | 'memberRoles'>,
  assigneeId: string,
  userId: UserId | null,
  actorUserId: UserId,
  priorMappingExists: boolean,
  /**
   * When the row also carries a Backlog link (`backlogUserId`), unbinding
   * the Migratus user must NOT delete the whole doc — that would silently
   * drop the Backlog→WBS mapping. Pass `true` so the unbind path clears
   * just `userId` and keeps the Backlog link. Defaults to `false`
   * (delete the doc) to preserve the original single-link behaviour.
   */
  priorBacklogLinkExists = false,
): Promise<SetMappingResult> {
  const ref = assigneeUserMapRef(db, project.projectId, assigneeId);
  if (userId === null) {
    if (priorBacklogLinkExists) {
      // Keep the doc (and its Backlog link); drop only the Migratus user.
      await updateDoc(ref, {
        userId: deleteField(),
        updatedAt: serverTimestamp(),
      });
    } else {
      await deleteDoc(ref);
    }
    return { ok: true };
  }

  // 1:1 enforcement: scan for any other mapping that already claims this user.
  const dupSnap = await getDocs(
    query(assigneeUserMapCol(db, project.projectId), where('userId', '==', userId)),
  );
  for (const d of dupSnap.docs) {
    if (d.id !== assigneeId) {
      return {
        ok: false,
        reason: 'duplicate',
        existingAssigneeId: d.id,
      };
    }
  }

  // Distinguish create vs update so the rebind path doesn't trip the
  // rules-side `fieldsUnchanged(['createdAt', 'createdByUserId'])`
  // immutability guard. Before this split, every rebind sent a new
  // `createdAt: serverTimestamp()` and a possibly-different
  // `createdByUserId`, so a second admin re-binding the same row hit
  // "Missing or insufficient permissions" — see bug 2 in the
  // assignee-mapping-fixes brief.
  const now = serverTimestamp();
  if (!priorMappingExists) {
    const createPayload: Omit<AssigneeUserMappingDoc, 'createdAt' | 'updatedAt'> & {
      createdAt: ReturnType<typeof serverTimestamp>;
      updatedAt: ReturnType<typeof serverTimestamp>;
    } = {
      assigneeId,
      projectId: project.projectId,
      userId,
      ...memberFieldsFromProject(project),
      createdAt: now,
      updatedAt: now,
      createdByUserId: actorUserId,
    };
    await setDoc(ref, createPayload, { merge: false });
    return { ok: true };
  }

  // Update path: only mutate the fields the rules-side update rule
  // permits. `createdAt` / `createdByUserId` stay frozen.
  await updateDoc(ref, {
    userId,
    updatedAt: now,
  });
  return { ok: true };
}

export type SetBacklogMappingResult =
  | { ok: true }
  | { ok: false; reason: 'duplicate'; existingAssigneeId: string };

/** Identity of a Backlog user to link, captured at pick time. */
export interface BacklogUserLink {
  id: number;
  /** Display name snapshot — denormalized for the UI, never matched on. */
  name: string;
}

/**
 * Link / relink / unlink an assignee to a **Backlog space user**, writing
 * `backlogUserId` / `backlogUserName` onto the shared `assigneeUserMap`
 * doc (keyed by `assigneeId`). Independent of the Migratus `userId` link:
 * a doc may carry either, both, or — after this — only the Backlog link.
 *
 * Pass `backlog === null` to unlink. The 1:1-from-the-Backlog-user-side
 * rule (one Backlog user maps to at most one assignee per project) is
 * enforced here via a `where('backlogUserId', '==')` pre-flight, mirroring
 * `setAssigneeUserMapping`'s user-side guard.
 *
 * `priorMappingExists` / `priorUserLinkExists` come from the live
 * `useAssigneeUserMappings` snapshot so the helper can branch
 * create/update/delete without a get() probe (same reasoning as the
 * sibling user-mapping helper).
 */
export async function setAssigneeBacklogUser(
  db: Firestore,
  project: Pick<ProjectDoc, 'projectId' | 'memberUserIds' | 'memberRoles'>,
  assigneeId: string,
  backlog: BacklogUserLink | null,
  actorUserId: UserId,
  priorMappingExists: boolean,
  priorUserLinkExists: boolean,
): Promise<SetBacklogMappingResult> {
  const ref = assigneeUserMapRef(db, project.projectId, assigneeId);

  if (backlog === null) {
    if (!priorMappingExists) return { ok: true };
    if (priorUserLinkExists) {
      // Keep the doc (and its Migratus user); drop only the Backlog link.
      await updateDoc(ref, {
        backlogUserId: deleteField(),
        backlogUserName: deleteField(),
        updatedAt: serverTimestamp(),
      });
    } else {
      await deleteDoc(ref);
    }
    return { ok: true };
  }

  // 1:1 enforcement: refuse to bind a Backlog user already claimed by
  // another assignee in this project.
  const dupSnap = await getDocs(
    query(
      assigneeUserMapCol(db, project.projectId),
      where('backlogUserId', '==', backlog.id),
    ),
  );
  for (const d of dupSnap.docs) {
    if (d.id !== assigneeId) {
      return { ok: false, reason: 'duplicate', existingAssigneeId: d.id };
    }
  }

  const now = serverTimestamp();
  if (!priorMappingExists) {
    const createPayload: Omit<
      AssigneeUserMappingDoc,
      'createdAt' | 'updatedAt' | 'userId'
    > & {
      createdAt: ReturnType<typeof serverTimestamp>;
      updatedAt: ReturnType<typeof serverTimestamp>;
    } = {
      assigneeId,
      projectId: project.projectId,
      backlogUserId: backlog.id,
      backlogUserName: backlog.name,
      ...memberFieldsFromProject(project),
      createdAt: now,
      updatedAt: now,
      createdByUserId: actorUserId,
    };
    await setDoc(ref, createPayload, { merge: false });
    return { ok: true };
  }

  // Update path: only the Backlog fields move; userId / createdAt /
  // createdByUserId stay frozen so the rules-side immutability guard and
  // any existing Migratus link are preserved.
  await updateDoc(ref, {
    backlogUserId: backlog.id,
    backlogUserName: backlog.name,
    updatedAt: now,
  });
  return { ok: true };
}

/**
 * Build a fast `assigneeId -> backlogUserId` (and name) lookup. Used by the
 * Settings page rows so each Backlog picker can show the current link.
 */
export function indexBacklogByAssigneeId(
  mappings: readonly AssigneeUserMappingDoc[],
): Map<string, { id: number; name: string | null }> {
  const m = new Map<string, { id: number; name: string | null }>();
  for (const r of mappings) {
    if (r.backlogUserId != null) {
      m.set(r.assigneeId, { id: r.backlogUserId, name: r.backlogUserName ?? null });
    }
  }
  return m;
}

/**
 * Build a fast `assigneeId -> userId` lookup from a list of mapping docs.
 * Used by hooks/components to resolve "who owns this assignee" without
 * scanning the array on every render.
 */
export function indexUserByAssigneeId(
  mappings: readonly AssigneeUserMappingDoc[],
): Map<string, UserId> {
  const m = new Map<string, UserId>();
  // Skip Backlog-only mappings (no Migratus userId) — they own no user.
  for (const r of mappings) if (r.userId) m.set(r.assigneeId, r.userId);
  return m;
}

/**
 * Inverse: `userId -> assigneeId`. Used by "my work" filters that take
 * the current Firebase UID and resolve the assignee identity before
 * filtering tasks.
 */
export function indexAssigneeIdByUser(
  mappings: readonly AssigneeUserMappingDoc[],
): Map<UserId, string> {
  const m = new Map<UserId, string>();
  // Skip Backlog-only mappings (no Migratus userId).
  for (const r of mappings) if (r.userId) m.set(r.userId, r.assigneeId);
  return m;
}

/**
 * Map a Firestore mutation error from the user-mapping write path to
 * a user-friendly Japanese message. The raw "Missing or insufficient
 * permissions" string from `firebase/firestore` is unhelpful in a
 * Japanese-only product (and confusing for admins who are *supposed*
 * to be allowed) — surface a clear "ask the admin" message instead.
 *
 * Pure / synchronous so it slots into either the picker's onError or
 * the section-level error toast without extra plumbing. Unknown
 * errors fall through to their original message so we don't hide
 * unrelated bugs (e.g. network errors) under a generic banner.
 */
export function localizeAssigneeMappingError(err: unknown): string {
  if (err == null) return '紐付けに失敗しました';
  // Firestore's JS SDK exposes the structured code on FirebaseError;
  // we duck-type it because importing the type just for `instanceof`
  // would force the whole `firebase` UMD into helpers callers.
  const code =
    (typeof err === 'object' && err !== null && 'code' in err
      ? String((err as { code: unknown }).code)
      : '') || '';
  const rawMessage =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : '紐付けに失敗しました';
  const looksLikePermissionDenied =
    code === 'permission-denied' ||
    code.endsWith('/permission-denied') ||
    /missing or insufficient permissions/i.test(rawMessage);
  if (looksLikePermissionDenied) {
    return '権限がありません。担当者の紐付けはオーナー / 管理者のみが編集できます。管理者に依頼してください。';
  }
  return rawMessage;
}

/**
 * Pure check for the 1:1-from-the-user-side rule, extracted so unit
 * tests can verify the guard without spinning up Firestore. Returns
 * the conflicting mapping when the proposed write would create a
 * duplicate, `null` when the write is OK.
 *
 * The runtime helper (`setAssigneeUserMapping`) reproduces this logic
 * via a `where('userId', '==')` query for freshness; this synchronous
 * helper exists for the in-memory cache path the Settings UI uses to
 * pre-grey conflicting options.
 */
export function findMappingConflict(
  mappings: readonly Pick<AssigneeUserMappingDoc, 'assigneeId' | 'userId'>[],
  candidateAssigneeId: string,
  candidateUserId: UserId | null,
): Pick<AssigneeUserMappingDoc, 'assigneeId' | 'userId'> | null {
  if (candidateUserId == null) return null;
  for (const m of mappings) {
    if (m.userId === candidateUserId && m.assigneeId !== candidateAssigneeId) {
      return m;
    }
  }
  return null;
}
